/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { PropertySetInputComponent } from '@vsolv/packages/properties/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Asset } from '@wsphere/assets/domain';
import { AssetService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-edit-asset-dialog',
  templateUrl: './edit-asset-dialog.component.html',
  standalone: false,
})
export class EditAssetDialog {
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private assetSvc: AssetService,
    private formBuilder: FormBuilder,
    private toastSvc: ToastService
  ) {}

  @ViewChild('dialog') dialog!: DialogComponent;

  @ViewChild('propertyValuesInput') propertyValuesInput?: PropertySetInputComponent;

  asset: Asset.Model | null = null;

  /** Staff / admin edit should include hidden property-set fields. */
  showHidden = false;

  @Output() closed = new EventEmitter();

  form = this.formBuilder.group({
    name: [null as string | null, Validators.required],
    propertyValues: [
      null as { propertyValues: Record<string, unknown>; dimensionValues: Record<string, unknown> } | null,
      Validators.required,
    ],
  });

  /**
   * Open the edit dialog for an asset. If editing within a provisioning sales session,
   * pass the sessionId so we fetch session-specific asset values (e.g. mileage).
   */
  async open(asset: Asset.Model, sessionId?: string, showHidden = false) {
    this.showHidden = showHidden;
    this.asset = null;
    this.form.reset();
    // Tear down the previous vs-property-set-input before mounting a new one.
    await new Promise(res => setTimeout(res, 1));

    await this.loadAssetValues(asset, sessionId);

    this.asset = asset;
    this.dialog.open();
    // Dialog.open() only flushes the overlay view; refresh this host so projected
    // name + property-set fields bind in the same turn (Angular 22 CDK overlay CD).
    this.cdr.detectChanges();
  }

  close(assetId?: string) {
    this.dialog.close();
    this.closed.emit(assetId);
    this.form.reset();
    this.asset = null;
    this.showHidden = false;
  }

  async submit() {
    if (!this.asset) return;

    const name = this.form.value.name ?? undefined;
    const formValues = this.form.value.propertyValues;

    if (!name || !formValues) return;

    const response = await this.assetSvc.updateAsset(this.asset.id, {
      name,
      propertyValues: formValues.propertyValues,
      dimensionValues: formValues.dimensionValues,
    });

    if (response?.id) {
      this.toastSvc.show({
        type: 'success',
        title: 'Asset update',
        text: `${name || this.asset.id} was successfully updated!`,
      });
      this.close(response.id);
    } else {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: `Failed to update the asset. Please verify the information and try again.`,
      });
    }
  }

  isValid(): boolean {
    if (!this.form.value.name) return false;

    if (!this.propertyValuesInput) return false;

    if (!this.propertyValuesInput.form?.value) return false;

    return this.propertyValuesInput.form?.valid;
  }

  private async loadAssetValues(asset: Asset.Model, sessionId?: string) {
    try {
      const latest = sessionId
        ? await this.assetSvc.getAssetValuesForSales(asset.id, sessionId)
        : await this.assetSvc.getAssetValues(asset.id);

      this.form.patchValue({
        name: asset.name,
        propertyValues: {
          propertyValues: (latest?.value ?? asset.values ?? {}) as any,
          dimensionValues: (latest?.dimensionValues ?? asset.dimensionValues ?? {}) as Record<string, unknown>,
        },
      });
    } catch {
      // Fall back to list-embedded values so the dialog still opens for editing.
      this.form.patchValue({
        name: asset.name,
        propertyValues: {
          propertyValues: (asset.values ?? {}) as any,
          dimensionValues: (asset.dimensionValues ?? {}) as Record<string, unknown>,
        },
      });
    }
  }
}
